/*
 * MarineMaker: Boat Name Studio  <mm-boat-name-studio>
 * Port of handoff/design-reference/Main.dc.html #studio (desktop) and MobileHome.dc.html (mobile):
 * D() data, measure(), current(), setMaterial(), savePreview() and renderVals() logic.
 *
 * Rules (handoff/CLAUDE.md, spec/custom-sections.md):
 * - No prices are computed here. The price slot only shows text returned by the section's pricing_endpoint.
 * - Network calls go only to the section's pricing_endpoint / proof_endpoint, and only when they are
 *   same-origin paths (App Proxy, e.g. /apps/marinemaker/price). Blank endpoint = placeholder / client-side PNG.
 * - The uploaded boat photo never leaves the browser (object URL, revoked when replaced or removed).
 */
(() => {
  'use strict';

  if (!window.customElements || window.customElements.get('mm-boat-name-studio')) return;

  /*
   * Lettering styles. This is the ONE place the five styles are defined (ids are stable: share URLs use them).
   * Production fonts are pending (OPEN-QUESTIONS #5). 'MM …' families are declared with @font-face in
   * sections/mm-boat-name-studio.liquid and point at assets/mm-font-*.woff2. Until those files exist the
   * stacks fall back to local/system faces that still differ per style.
   */
  const STYLES = [
    { id: 'transom', label: 'Transom Serif', family: "'MM Transom', 'DM Serif Display', 'Bodoni 72', Didot, 'Bodoni MT', 'Playfair Display', Georgia, 'Times New Roman', serif", weight: '400', style: 'normal' },
    { id: 'deck', label: 'Deck Sans', family: "'MM Deck', 'Archivo Black', 'Arial Black', 'Segoe UI Black', 'Helvetica Neue', Arial, sans-serif", weight: '800', style: 'normal' },
    { id: 'commodore', label: 'Commodore', family: "'MM Commodore', 'Zilla Slab', Rockwell, 'Rockwell Nova', 'Roboto Slab', 'Bookman Old Style', Georgia, serif", weight: '700', style: 'normal' },
    { id: 'commodoreItalic', label: 'Commodore Italic', family: "'MM Commodore', 'Zilla Slab', Rockwell, 'Rockwell Nova', 'Roboto Slab', 'Bookman Old Style', Georgia, serif", weight: '700', style: 'italic' },
    { id: 'script', label: 'Yacht Script', family: "'MM Script', 'Great Vibes', 'Snell Roundhand', 'Segoe Script', 'Brush Script MT', 'Apple Chancery', cursive", weight: '400', style: 'normal' }
  ];

  const SHADOW = 'drop-shadow(0.025em 0.03em 0 rgba(0,0,0,.55)) drop-shadow(0.05em 0.08em 0.06em rgba(0,0,0,.35))';
  const FINISHES = {
    acrylic: [
      { id: 'mirror-gold', label: 'Mirror Gold', swatch: 'linear-gradient(135deg,#6E5020,#F3DEA4 40%,#9C7631 60%,#E9CF8E 80%)', color: 'transparent', bg: 'linear-gradient(180deg,#F6E3AE 0%,#C9A35A 38%,#7A5A22 52%,#E3C584 74%,#9C7631 100%)', clip: true, filter: 'drop-shadow(0.025em 0.03em 0 #4A3510) drop-shadow(0.05em 0.08em 0.06em rgba(0,0,0,.35))', canvas: ['#F6E3AE', '#7A5A22', '#E3C584'] },
      { id: 'mirror-silver', label: 'Mirror Silver', swatch: 'linear-gradient(135deg,#6B7279,#F4F6F8 40%,#8D959C 60%,#E1E5E8 80%)', color: 'transparent', bg: 'linear-gradient(180deg,#FFFFFF 0%,#C9CFD4 38%,#6B7279 52%,#E6EAED 74%,#9AA2A9 100%)', clip: true, filter: 'drop-shadow(0.025em 0.03em 0 #3E444A) drop-shadow(0.05em 0.08em 0.06em rgba(0,0,0,.35))', canvas: ['#FFFFFF', '#6B7279', '#E6EAED'] },
      { id: 'gloss-black', label: 'Gloss Black', swatch: 'linear-gradient(160deg,#4A5058,#0B0D10 55%)', color: 'transparent', bg: 'linear-gradient(180deg,#4A5058 0%,#15181C 42%,#0B0D10 100%)', clip: true, filter: SHADOW, canvas: ['#4A5058', '#0B0D10', '#15181C'] },
      { id: 'gloss-white', label: 'Gloss White', swatch: 'linear-gradient(160deg,#FFFFFF,#E3E6E8)', color: 'transparent', bg: 'linear-gradient(180deg,#FFFFFF 0%,#F2F4F5 50%,#DADEE1 100%)', clip: true, filter: 'drop-shadow(0.025em 0.03em 0 #8F989F) drop-shadow(0.05em 0.08em 0.06em rgba(0,0,0,.3))', canvas: ['#FFFFFF', '#F2F4F5', '#DADEE1'] },
      { id: 'frosted', label: 'Frosted', swatch: 'linear-gradient(160deg,rgba(236,243,245,.95),rgba(190,204,209,.85))', color: 'transparent', bg: 'linear-gradient(180deg,rgba(240,246,248,.9) 0%,rgba(205,217,221,.8) 100%)', clip: true, filter: 'drop-shadow(0.02em 0.025em 0 rgba(110,128,134,.7)) drop-shadow(0.04em 0.06em 0.05em rgba(0,0,0,.2))', canvas: ['#F0F6F8', '#CDD9DD', '#E3ECEF'] }
    ],
    // STAND-INS: the vinyl colour list is not decided (handoff/docs/OPEN-QUESTIONS.md #3).
    // Replace these four entries once the owner confirms the production vinyl colours.
    vinyl: [
      { id: 'v-black', label: 'Black', swatch: '#15171A', color: '#15171A', bg: 'none', clip: false, filter: 'none', canvas: ['#15171A', '#15171A', '#15171A'] },
      { id: 'v-white', label: 'White', swatch: '#FFFFFF', color: '#F7F7F5', bg: 'none', clip: false, filter: 'none', canvas: ['#F7F7F5', '#F7F7F5', '#F7F7F5'] },
      { id: 'v-gold', label: 'Metallic Gold', swatch: 'linear-gradient(135deg,#8C6E3A,#D8BE86)', color: 'transparent', bg: 'linear-gradient(180deg,#D8BE86,#A88A50)', clip: true, filter: 'none', canvas: ['#D8BE86', '#A88A50', '#C4A86E'] },
      { id: 'v-silver', label: 'Metallic Silver', swatch: 'linear-gradient(135deg,#7D848B,#D9DDE0)', color: 'transparent', bg: 'linear-gradient(180deg,#E1E5E8,#9AA2A9)', clip: true, filter: 'none', canvas: ['#E1E5E8', '#9AA2A9', '#C3C9CE'] }
    ]
  };

  const MATERIALS = {
    acrylic: { label: 'Dimensional acrylic', note: 'Raised letters with real depth. Mirror, gloss and frosted finishes.' },
    vinyl: { label: 'Cut vinyl', note: 'Flat lettering applied directly to the surface.' }
  };

  const HULLS = [
    { id: 'white', label: 'White hull', c: '#F4F3EE', c2: '#D6D5CE' },
    { id: 'navy', label: 'Navy hull', c: '#1E3150', c2: '#0F1B2D' },
    { id: 'charcoal', label: 'Charcoal hull', c: '#43474D', c2: '#24272B' },
    { id: 'sea', label: 'Sea-grey hull', c: '#86A0A3', c2: '#5D7477' }
  ];

  const MAX_NAME = 18;
  const MAX_PORT = 28;
  const FALLBACK_TEXT = 'Your Name';
  // Boat names are priced on the proof (owner decision 2026-10-03). A pricing_endpoint, if set later, replaces this.
  const PRICE_PLACEHOLDER = 'Quoted on your proof';
  const PRICE_NOTE = 'Send your design and we confirm the price with your proof, before anything is cut.';
  const PRICE_ERROR_NOTE = 'Price confirmed with your proof';
  const SCALE_FACTOR = 82; // font-size (cqw) = (height / capRatio) / transom * 82

  const clean = (v, max) => String(v == null ? '' : v).replace(/[\u0000-\u001F\u007F]/g, '').slice(0, max);
  const validHeight = (n) => Number.isFinite(n) && n >= 3 && n <= 14 && Math.abs(n * 2 - Math.round(n * 2)) < 1e-9;
  const validTransom = (n) => Number.isFinite(n) && n >= 36 && n <= 240;
  // Only same-origin paths ("/apps/…"). Anything else (absolute or protocol-relative URL) is ignored.
  const safePath = (v) => { const p = String(v || '').trim(); return /^\/(?!\/)/.test(p) ? p : ''; };
  const reducedMotion = () => !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const fontString = (f, px) => f.style + ' ' + f.weight + ' ' + px + 'px ' + f.family;

  function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  }

  function loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  }

  class BoatNameStudio extends HTMLElement {
    connectedCallback() {
      if (!this._built) this.init();
      this.attachGlobal();
    }

    disconnectedCallback() {
      this.detachGlobal();
      // Theme editor re-renders replace the element; only free the photo if it is really gone.
      setTimeout(() => { if (!this.isConnected) this.revokePhoto(); }, 0);
    }

    /* ---------- setup ---------- */

    init() {
      this._built = true;
      this.$ = {};
      this.querySelectorAll('[data-ref]').forEach((el) => { this.$[el.dataset.ref] = el; });

      const d = this.dataset;
      this.cfg = {
        pricing: safePath(d.pricingEndpoint),
        proof: safePath(d.proofEndpoint),
        quoteUrl: d.quoteUrl || '/pages/request-a-quote',
        shareUrl: d.shareUrl || (window.location.origin + '/pages/boat-name-studio')
      };

      const t = parseFloat(d.defaultTransom);
      const transom = validTransom(t) ? t : 96;
      this.s = {
        name: clean(d.defaultName, MAX_NAME).trim(),
        port: clean(d.defaultPort, MAX_PORT).trim(),
        material: 'acrylic', style: 'transom', finish: 'mirror-gold',
        height: 6, transom, transomRaw: String(transom),
        hull: 'white', photo: null, nameY: 45,
        finishNote: '', shareOpen: false
      };
      this.restoreFromUrl();

      this.open = { name: true }; // mobile accordion: Name open first (MobileHome.dc.html)
      this.price = { state: this.cfg.pricing ? 'loading' : 'placeholder', text: '' };
      this._mcache = new Map();
      this._finMat = null;
      this.cqw = !!(window.CSS && CSS.supports && CSS.supports('width', '1cqw'));

      this.buildStyles();
      this.buildHulls();
      this.syncInputs();
      this.bindEvents();
      this.render();
      this.waitForFonts();
    }

    attachGlobal() {
      if (this._attached) return;
      this._attached = true;

      this.mq = window.matchMedia('(max-width: 768px)');
      this._onMq = () => this.applyMode();
      if (this.mq.addEventListener) this.mq.addEventListener('change', this._onMq);
      else if (this.mq.addListener) this.mq.addListener(this._onMq);
      this.applyMode();

      // Sticky quote bar (mobile): only while the studio is on screen.
      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver((entries) => {
          this._inView = entries[entries.length - 1].isIntersecting;
          this.updateBar();
        });
        this._io.observe(this);
      } else {
        this._inView = true;
        this.updateBar();
      }

      if (!this.cqw && 'ResizeObserver' in window && this.$.preview) {
        this._ro = new ResizeObserver(() => this.render());
        this._ro.observe(this.$.preview);
      }
    }

    detachGlobal() {
      if (!this._attached) return;
      this._attached = false;
      if (this.mq) {
        if (this.mq.removeEventListener) this.mq.removeEventListener('change', this._onMq);
        else if (this.mq.removeListener) this.mq.removeListener(this._onMq);
      }
      if (this._io) this._io.disconnect();
      if (this._ro) this._ro.disconnect();
      if (document.fonts && this._onFonts) document.fonts.removeEventListener('loadingdone', this._onFonts);
      clearTimeout(this._priceTimer);
      if (this._priceAbort) this._priceAbort.abort();
    }

    // Share link: ?name=&port=&material=&style=&finish=&height=&transom=  Every value is validated; bad ones are ignored.
    restoreFromUrl() {
      let p;
      try { p = new URLSearchParams(window.location.search); } catch (e) { return; }
      const s = this.s;
      if (p.has('name')) { const v = clean(p.get('name'), MAX_NAME).trim(); if (v) s.name = v; }
      if (p.has('port')) s.port = clean(p.get('port'), MAX_PORT).trim();
      const mat = p.get('material');
      if (mat === 'acrylic' || mat === 'vinyl') s.material = mat;
      const st = p.get('style');
      if (STYLES.some((f) => f.id === st)) s.style = st;
      const fins = FINISHES[s.material];
      const fi = p.get('finish');
      if (fins.some((f) => f.id === fi)) s.finish = fi;
      else if (!fins.some((f) => f.id === s.finish)) s.finish = fins[0].id;
      if (p.has('height')) { const h = Number(p.get('height')); if (validHeight(h)) s.height = h; }
      if (p.has('transom')) { const t = Number(p.get('transom')); if (validTransom(t)) { s.transom = t; s.transomRaw = String(t); } }
    }

    buildStyles() {
      const box = this.$.styleOpts;
      if (!box) return;
      box.textContent = '';
      STYLES.forEach((f) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'mm-bns__opt';
        b.dataset.action = 'style';
        b.dataset.value = f.id;
        const sample = document.createElement('span');
        sample.className = 'mm-bns__opt-sample';
        sample.setAttribute('aria-hidden', 'true');
        sample.style.fontFamily = f.family;
        sample.style.fontWeight = f.weight;
        sample.style.fontStyle = f.style;
        const label = document.createElement('span');
        label.className = 'mm-bns__opt-label';
        label.textContent = f.label;
        b.append(sample, label);
        box.appendChild(b);
      });
    }

    buildFinishes() {
      const box = this.$.finishOpts;
      if (!box || this._finMat === this.s.material) return;
      this._finMat = this.s.material;
      box.textContent = '';
      FINISHES[this.s.material].forEach((f) => box.appendChild(this.swatchButton('finish', f.id, f.label, f.swatch)));
    }

    buildHulls() {
      const box = this.$.hullOpts;
      if (!box) return;
      box.textContent = '';
      HULLS.forEach((h) => box.appendChild(this.swatchButton('hull', h.id, h.label, h.c)));
    }

    swatchButton(action, value, label, swatch) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'mm-bns__sw';
      b.dataset.action = action;
      b.dataset.value = value;
      const dot = document.createElement('span');
      dot.className = 'mm-bns__sw-dot';
      dot.setAttribute('aria-hidden', 'true');
      dot.style.background = swatch;
      b.append(dot, document.createTextNode(label));
      return b;
    }

    syncInputs() {
      const s = this.s, $ = this.$;
      if ($.nameInput) $.nameInput.value = s.name;
      if ($.portInput) $.portInput.value = s.port;
      if ($.heightInput) $.heightInput.value = String(s.height);
      if ($.transomInput) $.transomInput.value = s.transomRaw;
      if ($.nameYInput) $.nameYInput.value = String(s.nameY);
    }

    bindEvents() {
      this.addEventListener('click', (e) => {
        const t = e.target.closest('[data-action], [data-acc]');
        if (!t || !this.contains(t)) return;
        if (t.hasAttribute('data-acc')) { this.toggleGroup(t.dataset.acc); return; }
        const v = t.dataset.value;
        switch (t.dataset.action) {
          case 'material': this.setMaterial(v); break;
          case 'style': if (STYLES.some((f) => f.id === v)) this.set({ style: v }); break;
          case 'finish': if (FINISHES[this.s.material].some((f) => f.id === v)) this.set({ finish: v, finishNote: '' }); break;
          case 'hull': if (HULLS.some((h) => h.id === v)) { this.revokePhoto(); this.set({ hull: v, photo: null }); } break;
          case 'remove-photo':
            this.revokePhoto();
            this.set({ photo: null });
            if (this.$.fileInput) this.$.fileInput.focus();
            break;
          case 'save': this.savePreview(); break;
          case 'share': this.toggleShare(t); break;
          case 'copy': this.copyLink(); break;
          default: break;
        }
      });

      this.addEventListener('input', (e) => {
        const t = e.target;
        switch (t.dataset.ref) {
          case 'nameInput': this.set({ name: clean(t.value, MAX_NAME) }); break;
          case 'portInput': this.set({ port: clean(t.value, MAX_PORT) }); break;
          case 'heightInput': { const h = parseFloat(t.value); if (validHeight(h)) this.set({ height: h }); break; }
          case 'transomInput': {
            const n = parseFloat(t.value);
            this.s.transomRaw = t.value;
            if (validTransom(n)) this.s.transom = n;
            this.render();
            break;
          }
          case 'nameYInput': { const y = parseInt(t.value, 10); if (y >= 10 && y <= 90) this.set({ nameY: y }); break; }
          default: break;
        }
      });

      this.addEventListener('change', (e) => {
        if (e.target === this.$.fileInput) this.onPhoto(e.target);
        if (e.target === this.$.transomInput && !validTransom(parseFloat(e.target.value))) this.render();
      });

      this.addEventListener('focusin', (e) => {
        if (e.target === this.$.shareInput) e.target.select();
        // Hide the mobile bar while typing so it can't sit on top of the field / on-screen keyboard.
        this._typing = e.target.matches('input:not([readonly]):is(:not([type]), [type="text"], [type="number"])');
        this.updateBar();
      });
      this.addEventListener('focusout', () => { this._typing = false; this.updateBar(); });

      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.s.shareOpen && this.$.share && this.$.share.contains(e.target)) {
          this.toggleShare(this._shareOpener || this.$.shareBtn);
        }
      });
    }

    /* ---------- fonts and measuring ---------- */

    waitForFonts() {
      if (!document.fonts) return;
      const refresh = () => { this._mcache.clear(); this.render(); };
      this._onFonts = refresh;
      document.fonts.addEventListener('loadingdone', refresh);
      const loads = STYLES.map((f) => document.fonts.load(fontString(f, 100), 'HHalcyon').catch(() => null));
      Promise.all(loads).then(() => document.fonts.ready).then(refresh, refresh);
    }

    // Port of measure(): text width and cap height ('H' ascent) at 100px.
    measure(f, text) {
      const key = f.id + '|' + text;
      if (this._mcache.has(key)) return this._mcache.get(key);
      let m;
      try {
        if (!this._cv) this._cv = document.createElement('canvas');
        const ctx = this._cv.getContext('2d');
        ctx.font = fontString(f, 100);
        const w = ctx.measureText(text).width;
        const cap = ctx.measureText('H').actualBoundingBoxAscent || 70;
        m = { w, cap };
      } catch (e) {
        m = { w: text.length * 62, cap: 70 };
      }
      this._mcache.set(key, m);
      return m;
    }

    // Port of current()
    current() {
      const s = this.s;
      const font = STYLES.find((f) => f.id === s.style) || STYLES[0];
      const fins = FINISHES[s.material];
      const fin = fins.find((x) => x.id === s.finish) || fins[0];
      const text = s.name.trim() || FALLBACK_TEXT;
      const m = this.measure(font, text);
      const capRatio = Math.max(0.4, m.cap / 100);
      const widthIn = (m.w / m.cap) * s.height;
      const hull = HULLS.find((h) => h.id === s.hull) || HULLS[0];
      return { font, fin, fins, text, capRatio, widthIn, hull };
    }

    // Design object shared with mm-quote-request and the pricing endpoint (spec/custom-sections.md).
    design(c) {
      c = c || this.current();
      const s = this.s;
      return {
        name: s.name.trim(),
        port: s.port.trim(),
        material: s.material,
        style: c.font.id,
        finish: c.fin.id,
        height_in: s.height,
        transom_in: s.transom,
        approx_width_in: Math.round(c.widthIn * 10) / 10
      };
    }

    shareUrl(c) {
      c = c || this.current();
      const s = this.s;
      const p = new URLSearchParams();
      if (s.name.trim()) p.set('name', s.name.trim());
      p.set('port', s.port.trim());
      p.set('material', s.material);
      p.set('style', c.font.id);
      p.set('finish', c.fin.id);
      p.set('height', String(s.height));
      p.set('transom', String(s.transom));
      return this.cfg.shareUrl + '?' + p.toString();
    }

    quoteHref(c) {
      const sep = this.cfg.quoteUrl.indexOf('?') === -1 ? '?' : '&';
      return this.cfg.quoteUrl + sep + 'type=name&design=' + encodeURIComponent(JSON.stringify(this.design(c)));
    }

    /* ---------- state changes ---------- */

    set(patch) {
      Object.assign(this.s, patch);
      this.render();
    }

    // Port of setMaterial(): an incompatible finish is reset and the change is announced (aria-live).
    setMaterial(mat) {
      const s = this.s;
      if (!FINISHES[mat] || mat === s.material) return;
      const fins = FINISHES[mat];
      const keep = fins.some((f) => f.id === s.finish);
      const prev = FINISHES[s.material].find((f) => f.id === s.finish);
      this.set({
        material: mat,
        finish: keep ? s.finish : fins[0].id,
        finishNote: keep ? '' : (prev ? prev.label + ' isn’t available in ' + (mat === 'vinyl' ? 'vinyl' : 'acrylic') + '. Finish set to ' + fins[0].label + '.' : '')
      });
    }

    onPhoto(input) {
      const f = input.files && input.files[0];
      if (!f) return;
      if (f.type && f.type.indexOf('image/') !== 0) {
        this.status('Choose an image file.');
        input.value = '';
        return;
      }
      this.revokePhoto();
      this.set({ photo: URL.createObjectURL(f), nameY: 45 });
      if (this.$.nameYInput) this.$.nameYInput.value = '45';
      input.value = '';
    }

    revokePhoto() {
      if (this.s && this.s.photo) {
        try { URL.revokeObjectURL(this.s.photo); } catch (e) { /* ignore */ }
        this.s.photo = null;
      }
    }

    toggleGroup(key) {
      this.open[key] = !this.open[key];
      this.applyMode();
    }

    applyMode() {
      this.mobile = !!(this.mq && this.mq.matches);
      this.querySelectorAll('[data-acc]').forEach((btn) => {
        const k = btn.dataset.acc;
        const isOpen = !!this.open[k];
        const panel = this.querySelector('#' + CSS.escape(btn.getAttribute('aria-controls')));
        btn.setAttribute('aria-expanded', String(isOpen));
        const sign = btn.querySelector('.mm-bns__acc-sign');
        if (sign) sign.textContent = isOpen ? '−' : '+';
        if (panel) panel.hidden = this.mobile && !isOpen;
      });
      this.updateBar();
    }

    updateBar() {
      const bar = this.$.bar;
      if (!bar) return;
      const show = !!(this.mobile && this._inView && !this._typing);
      if (!show && bar.contains(document.activeElement)) return; // never hide the control that has focus
      bar.hidden = !show;
    }

    toggleShare(opener) {
      const open = !this.s.shareOpen;
      this._shareOpener = open ? opener : null;
      this.set({ shareOpen: open });
      if (open && opener && opener !== this.$.shareBtn && this.$.shareInput) {
        // Opened from the mobile bar: bring the panel into view and move focus to the link.
        this.$.share.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
        this.$.shareInput.focus({ preventScroll: true });
      } else if (!open && opener && this.$.share && this.$.share.contains(document.activeElement)) {
        opener.focus();
      }
    }

    status(msg) {
      if (!this.$.status) return;
      this.$.status.textContent = '';
      // Re-set after a tick so repeated identical messages are announced again.
      clearTimeout(this._statusTimer);
      this._statusTimer = setTimeout(() => { this.$.status.textContent = msg; }, 50);
    }

    async copyLink() {
      const url = this.shareUrl();
      try {
        await navigator.clipboard.writeText(url);
        this.status('Design link copied.');
      } catch (e) {
        const inp = this.$.shareInput;
        let ok = false;
        if (inp) {
          inp.focus();
          inp.select();
          try { ok = document.execCommand('copy'); } catch (err) { ok = false; }
        }
        this.status(ok ? 'Design link copied.' : 'Select the link and copy it.');
      }
    }

    /* ---------- pricing (never computed here) ---------- */

    // POST <pricing_endpoint>, body = design object (JSON). Expected response: { "price_text": "…" }.
    schedulePrice(design) {
      if (!this.cfg.pricing) return;
      const key = JSON.stringify(design);
      if (key === this._priceKey) return;
      this._priceKey = key;
      clearTimeout(this._priceTimer);
      this.price = { state: 'loading', text: '' };
      this._priceTimer = setTimeout(() => this.fetchPrice(design, key), 500);
    }

    async fetchPrice(design, key) {
      if (this._priceAbort) this._priceAbort.abort();
      const ac = ('AbortController' in window) ? new AbortController() : null;
      this._priceAbort = ac;
      try {
        const res = await fetch(this.cfg.pricing, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(design),
          signal: ac ? ac.signal : undefined
        });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const data = await res.json();
        const text = data && typeof data.price_text === 'string' ? data.price_text.trim().slice(0, 80) : '';
        if (!text) throw new Error('No price_text');
        if (key !== this._priceKey) return;
        this.price = { state: 'ok', text };
      } catch (e) {
        if (e && e.name === 'AbortError') return;
        if (key !== this._priceKey) return;
        this.price = { state: 'error', text: '' };
      }
      this.renderPrice();
    }

    renderPrice() {
      const v = this.$.priceValue, n = this.$.priceNote, cell = this.$.priceCell;
      if (!v || !n) return;
      const st = this.price.state;
      if (cell) cell.setAttribute('aria-busy', String(st === 'loading'));
      v.textContent = st === 'ok' ? this.price.text : (st === 'loading' ? 'Updating…' : PRICE_PLACEHOLDER);
      n.textContent = st === 'error' ? PRICE_ERROR_NOTE : PRICE_NOTE;
    }

    /* ---------- preview image (savePreview port) ---------- */

    filename(c) {
      const slug = c.text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'design';
      return 'marinemaker-' + slug + '-preview.png';
    }

    async savePreview() {
      if (this._saving) return;
      this._saving = true;
      const btn = this.$.saveBtn;
      if (btn) btn.setAttribute('aria-disabled', 'true');
      const c = this.current();
      try {
        if (this.cfg.proof) {
          try {
            await this.requestProof(c);
            this.status('Preview image saved.');
            return;
          } catch (e) { /* fall back to the client-side PNG below */ }
        }
        await this.drawPreviewPng(c);
        this.status('Preview image saved.');
      } catch (e) {
        this.status('We couldn’t create the preview image in this browser. You can share the design link instead.');
      } finally {
        this._saving = false;
        if (btn) btn.removeAttribute('aria-disabled');
      }
    }

    // POST <proof_endpoint>, body = design object. Response: an image (downloaded) or JSON { "url": "/…" | "https://…" }.
    async requestProof(c) {
      const res = await fetch(this.cfg.proof, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'image/png, application/json' },
        body: JSON.stringify(this.design(c))
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const type = res.headers.get('content-type') || '';
      if (type.indexOf('image/') === 0) {
        downloadBlob(await res.blob(), this.filename(c));
        return;
      }
      const data = await res.json();
      const url = data && typeof data.url === 'string' ? data.url : '';
      if (!/^(\/(?!\/)|https:\/\/)/.test(url)) throw new Error('No proof url');
      const a = document.createElement('a');
      a.href = url;
      a.download = this.filename(c);
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    }

    async drawPreviewPng(c) {
      const s = this.s;
      const W = 1600, H = 1000;
      const cv = document.createElement('canvas');
      cv.width = W;
      cv.height = H;
      const x = cv.getContext('2d');
      const px = (s.height / c.capRatio) / s.transom * (W * (SCALE_FACTOR / 100));

      if (document.fonts && document.fonts.load) {
        try { await document.fonts.load(fontString(c.font, Math.round(px)), c.text); } catch (e) { /* use fallback */ }
      }

      if (s.photo) {
        const img = await loadImage(s.photo);
        const r = Math.max(W / img.width, H / img.height);
        x.drawImage(img, (W - img.width * r) / 2, (H - img.height * r) / 2, img.width * r, img.height * r);
      } else {
        const hull = c.hull;
        let g = x.createLinearGradient(0, 0, 0, H);
        g.addColorStop(0, '#DCD6C9'); g.addColorStop(0.62, '#CFC8B9'); g.addColorStop(1, '#BFB8A8');
        x.fillStyle = g; x.fillRect(0, 0, W, H);
        g = x.createLinearGradient(0, H * 0.8, 0, H);
        g.addColorStop(0, '#2C4A5C'); g.addColorStop(1, '#1B3242');
        x.fillStyle = g; x.fillRect(0, H * 0.8, W, H * 0.2);
        x.fillStyle = '#2A2F36'; x.fillRect(W * 0.07, H * 0.135, W * 0.86, H * 0.026);
        g = x.createLinearGradient(0, H * 0.16, 0, H * 0.78);
        g.addColorStop(0, hull.c); g.addColorStop(1, hull.c2);
        x.fillStyle = g;
        x.beginPath();
        x.moveTo(W * 0.08, H * 0.16); x.lineTo(W * 0.92, H * 0.16);
        x.lineTo(W * 0.8696, H * 0.78); x.lineTo(W * 0.1304, H * 0.78);
        x.closePath(); x.fill();
        x.fillStyle = '#0FB5A9'; x.fillRect(W * 0.122, H * 0.69, W * 0.756, H * 0.016);
        x.fillStyle = '#8C6A47'; x.fillRect(W * 0.04, H * 0.78, W * 0.92, H * 0.032);
      }

      const y = H * (s.photo ? s.nameY / 100 : 0.45);
      x.textAlign = 'center';
      x.textBaseline = 'middle';
      x.font = fontString(c.font, px);
      const g = x.createLinearGradient(0, y - px / 2, 0, y + px / 2);
      g.addColorStop(0, c.fin.canvas[0]); g.addColorStop(0.5, c.fin.canvas[1]); g.addColorStop(1, c.fin.canvas[2]);
      if (s.material === 'acrylic') {
        x.shadowColor = 'rgba(0,0,0,.4)'; x.shadowOffsetX = px * 0.04; x.shadowOffsetY = px * 0.06; x.shadowBlur = px * 0.04;
      }
      x.fillStyle = g;
      x.fillText(c.text, W / 2, y);
      if (s.port.trim()) {
        x.font = fontString(c.font, px * 0.4);
        x.fillText(s.port.trim(), W / 2, y + px * 0.85);
      }
      x.shadowColor = 'transparent';
      x.shadowOffsetX = 0; x.shadowOffsetY = 0; x.shadowBlur = 0;

      const mono = (getComputedStyle(this).getPropertyValue('--navigation-font-family') || '').trim() || 'ui-monospace, monospace';
      x.fillStyle = 'rgba(5,6,8,.85)';
      x.fillRect(0, H - 56, W, 56);
      x.fillStyle = '#E6EBF0';
      x.font = '500 20px ' + mono;
      x.textAlign = 'left';
      x.fillText('MarineMaker design preview · ' + c.font.label + ' · ' + c.fin.label + ' · ' + s.height + ' in · approx. ' + c.widthIn.toFixed(1) + ' in wide', 24, H - 28);

      const blob = await new Promise((resolve) => cv.toBlob(resolve, 'image/png'));
      if (!blob) throw new Error('toBlob failed');
      downloadBlob(blob, this.filename(c));
    }

    /* ---------- render (port of renderVals) ---------- */

    render() {
      if (!this.s) return;
      const s = this.s, $ = this.$;
      const c = this.current();
      const font = c.font, fin = c.fin;
      const fontCqw = (s.height / c.capRatio) / s.transom * SCALE_FACTOR;
      const heightLabel = s.height.toFixed(1) + ' in';
      const widthLabel = c.widthIn.toFixed(1) + ' in';
      const portText = s.port.trim();
      const setText = (el, t) => { if (el && el.textContent !== t) el.textContent = t; };

      // Preview
      const size = (k) => this.cqw ? (fontCqw * k).toFixed(3) + 'cqw'
        : ((fontCqw * k) * (($.preview && $.preview.clientWidth) || 800) / 100).toFixed(2) + 'px';
      [[$.nameText, 1, c.text], [$.portText, 0.4, portText]].forEach(([el, k, t]) => {
        if (!el) return;
        setText(el, t);
        el.style.fontFamily = font.family;
        el.style.fontWeight = font.weight;
        el.style.fontStyle = k === 1 ? font.style : 'normal';
        el.style.fontSize = size(k);
        el.style.color = fin.color;
        el.style.backgroundImage = fin.bg;
        el.style.filter = fin.filter;
        el.classList.toggle('mm-bns__clip', !!fin.clip);
      });
      if ($.portText) $.portText.hidden = !portText;
      if ($.lettering) $.lettering.style.top = (s.photo ? s.nameY : 43) + '%';
      if ($.hullBody) $.hullBody.style.background = 'linear-gradient(180deg,' + c.hull.c + ' 0%,' + c.hull.c2 + ' 100%)';
      if ($.hull) $.hull.hidden = !!s.photo;
      if ($.photo) {
        if (s.photo) { if ($.photo.getAttribute('src') !== s.photo) $.photo.src = s.photo; $.photo.hidden = false; }
        else { $.photo.hidden = true; $.photo.removeAttribute('src'); }
      }
      setText($.scale, 'Scale · ' + s.transom + ' in transom');
      if ($.preview) {
        $.preview.setAttribute('aria-label', 'Preview: ' + c.text + (portText ? ', hailing port ' + portText : '') + ', in ' + font.label + ', ' + fin.label + ', ' + s.height + ' inch letters' + (s.photo ? ' on your uploaded photo' : ' on a ' + c.hull.label.toLowerCase()));
      }

      // Readouts
      setText($.width, widthLabel);
      setText($.widthCm, '≈ ' + (c.widthIn * 2.54).toFixed(0) + ' cm');
      setText($.heightOut, heightLabel);
      setText($.heightRead, heightLabel);
      setText($.styleRead, font.label);
      setText($.finishRead, fin.label);
      if ($.tooWide) {
        const tooWide = c.widthIn > s.transom * 0.85;
        $.tooWide.hidden = !tooWide;
        setText($.tooWide, tooWide ? 'At this height the name covers more than 85% of a ' + s.transom + '-inch transom. Try a smaller height, or we’ll advise on fit when we review your photo.' : '');
      }

      // Hand-off links
      const href = this.quoteHref(c);
      if ($.quoteLink) $.quoteLink.href = href;
      if ($.quoteLinkBar) $.quoteLinkBar.href = href;
      if ($.shareInput) { const u = this.shareUrl(c); if ($.shareInput.value !== u) $.shareInput.value = u; }
      if ($.share) $.share.hidden = !s.shareOpen;
      this.querySelectorAll('[data-action="share"]').forEach((b) => b.setAttribute('aria-expanded', String(!!s.shareOpen)));

      // Name / port
      setText($.count, s.name.length + '/' + MAX_NAME);
      if ($.nameInput && document.activeElement !== $.nameInput && $.nameInput.value !== s.name) $.nameInput.value = s.name;

      // Material
      this.querySelectorAll('[data-action="material"]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.value === s.material)));
      setText($.matNote, MATERIALS[s.material].note);
      setText($.finishNote, s.finishNote);

      // Styles: each option renders the current name in its own font
      const sample = c.text.length > 11 ? c.text.slice(0, 10) + '…' : c.text;
      if ($.styleOpts) {
        $.styleOpts.querySelectorAll('.mm-bns__opt').forEach((b) => {
          b.setAttribute('aria-pressed', String(b.dataset.value === font.id));
          setText(b.querySelector('.mm-bns__opt-sample'), sample);
        });
      }

      // Finishes
      this.buildFinishes();
      if ($.finishOpts) $.finishOpts.querySelectorAll('[data-action="finish"]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.value === fin.id)));

      // Size
      if ($.heightInput) {
        $.heightInput.setAttribute('aria-valuetext', heightLabel);
        if (parseFloat($.heightInput.value) !== s.height) $.heightInput.value = String(s.height);
      }
      if ($.transomInput) {
        const bad = !validTransom(parseFloat(s.transomRaw));
        $.transomInput.setAttribute('aria-invalid', String(bad));
        if ($.transomErr) { $.transomErr.hidden = !bad; setText($.transomErr, bad ? 'Enter a width from 36 to 240 inches.' : ''); }
      }

      // Preview on: hulls / photo
      if ($.hullOpts) $.hullOpts.querySelectorAll('[data-action="hull"]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.value === s.hull && !s.photo)));
      if ($.removePhoto) $.removePhoto.hidden = !s.photo;
      if ($.nameYField) $.nameYField.hidden = !s.photo;

      // Mobile accordion summaries
      setText($.accName, c.text);
      setText($.accMaterial, MATERIALS[s.material].label);
      setText($.accStyle, font.label);
      setText($.accFinish, fin.label);
      setText($.accSize, heightLabel);

      // Price slot
      this.schedulePrice(this.design(c));
      this.renderPrice();
    }
  }

  window.customElements.define('mm-boat-name-studio', BoatNameStudio);
})();
